import * as Yup from 'yup'

import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { requestPassword } from '../redux/AuthCRUD'

import { Auth } from 'aws-amplify'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { useAuth } from 'app/context/auth-context'
import { VJFPUserService } from 'app/services/VJFPUserService'

type Params = {
    email: string;
}

const initialValues = {
    email: '',
    code: '',
    password: '',
    changepassword: ''
}

const forgotPasswordCodeVerificationSchema = Yup.object().shape({
    email: Yup.string()
        .email('Wrong email format')
        .min(3, 'Minimum 3 symbols')
        .max(100, 'Maximum 100 symbols')
        .required('Email is required'),
    code: Yup.string()
        .min(3, 'Minimum 3 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('Code is required'),
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters long')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/\d/, 'Password must contain at least one number')
        .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character')
        .required('Password is required'),
    changepassword: Yup.string()
        .required('Password confirmation is required')
        .when('password', {
            is: (val: string) => (val && val.length > 0 ? true : false),
            then: Yup.string().oneOf([Yup.ref('password')], "Password and Confirm Password didn't match"),
        }),
})

export function ForgotPasswordCodeVerification() {
    const [loading, setLoading] = useState(false)
    const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [resendTimer, setResendTimer] = useState(30)
    const [resendAttempts, setResendAttempts] = useState(0)
    const [resendLoading, setResendLoading] = useState(false)
    const togglePasswordVisibility = () => {
        setShowPassword(prev => !prev);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(prev => !prev);
    };
    const params = useParams<Params>();
    const search = useLocation().search;
    const orgId = new URLSearchParams(search).get("orgId");
    const jobfairId = new URLSearchParams(search).get("jobfairId");
    const history = useHistory();
    const { login } = useAuth();

    const formik = useFormik({
        initialValues: { ...initialValues, email: decodeURIComponent(params.email) },
        validationSchema: forgotPasswordCodeVerificationSchema,
        onSubmit: (values, { setStatus, setSubmitting }) => {
            setLoading(true)
            setHasErrors(undefined);

            Auth.forgotPasswordSubmit(values.email, values.code, values.password)
                .then(async () => {
                    setHasErrors(false);

                    if (orgId && jobfairId) {
                        await login(values.email, values.password);
                        history.push(`/job-seeker/organizations/${orgId}/jobfairs/${jobfairId}/lobby`);
                    } else {
                        window.location.href = `/auth/login`;
                    }
                    setLoading(false);
                })
                .catch((err) => {
                    console.log(err);
                    if (err.code === 'InvalidPasswordException') {
                        formik.setFieldError('password', 'Password does not conform to policy: Password must have symbol characters')
                    } else {
                        setHasErrors(true)
                        setStatus('The verification code entered is incorrect')
                    }
                    setLoading(false);
                    setSubmitting(false);
                    setStatus('The verification code entered is incorrect');
                })
        }
    });

    // Resend timer effect
    useEffect(() => {
        if (resendTimer > 0) {
            const timerId = setInterval(() => {
                setResendTimer(prev => prev - 1)
            }, 1000)
            return () => clearInterval(timerId)
        }
    }, [resendTimer])

    const handleResendCode = useCallback(async () => {
        if (resendAttempts >= 5) {
            formik.setStatus('Maximum resend attempts reached. Please try again later.')
            return
        }

        const email = formik.values.email
        if (!email) {
            formik.setStatus('Email is required to resend code.')
            return
        }

        setResendLoading(true)
        try {
            const user = await new VJFPUserService().getByEmail(email);
            if (!user?.username) {
                formik.setStatus('User not found.')
                return
            }
            await requestPassword(user.username)
            setResendTimer(30)
            setResendAttempts(prev => prev + 1)
            formik.setStatus('Verification code resent successfully. Please check your email.')
            setHasErrors(false)
        } catch (error: any) {
            console.error('Error resending code:', error)
            formik.setStatus(error.message || 'Failed to resend code. Please try again.')
            setHasErrors(true)
        } finally {
            setResendLoading(false)
        }
    }, [formik, resendAttempts])

    return (
        <div className='w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
            <form
                className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
                noValidate
                id='kt_login_password_reset_form'
                onSubmit={formik.handleSubmit}
            >
                <div className='text-center mb-10'>
                    {/* begin::Title */}
                    <h1 className='text-dark mb-3'>Reset Password</h1>
                    {/* end::Title */}

                    {/* begin::Link */}
                    <div className='mb-lg-15 alert alert-info'>
                        <div className='alert-text font-weight-bold'>
                            Sent password reset code. Please check your email.
                        </div>
                    </div>

                    <div className='mb-10'>
                        {resendTimer > 0 ? (
                            <span className='text-muted'>
                                Resend code in <span className='fw-bold'>{resendTimer}s</span>
                            </span>
                        ) : (
                            <button
                                type='button'
                                className='btn btn-link p-0'
                                onClick={handleResendCode}
                                disabled={resendLoading || resendAttempts >= 5}
                            >
                                {resendLoading ? 'Resending...' : 'Resend Confirmation Code'}
                            </button>
                        )}
                        {resendAttempts > 0 && resendAttempts < 5 && (
                            <div className='text-muted mt-1 small'>
                                Attempt {resendAttempts} of 5
                            </div>
                        )}
                        {resendAttempts >= 5 && (
                            <div className='text-danger mt-1 small'>
                                Maximum resend attempts reached.
                            </div>
                        )}
                    </div>
                    {/* end::Link */}
                </div>

                {/* begin::Title */}
                {formik.status && (
                    <div className={clsx('mb-lg-15 alert', formik.status.includes('successfully') ? 'alert-info' : 'alert-danger')}>
                        <div className='alert-text font-weight-bold'>{formik.status}</div>
                    </div>
                )}

                {hasErrors === true && (
                    <div className='mb-lg-15 alert alert-danger'>
                        <div className='alert-text font-weight-bold'>
                            Sorry, looks like there are some errors detected, please try again.
                        </div>
                    </div>
                )}

                {hasErrors === false && (
                    <div className='mb-10 bg-light-info p-8 rounded'>
                        <div className='text-info'>Password reset is successful</div>
                    </div>
                )}
                {/* end::Title */}

                {/* begin::Form group Email */}
                <div className='fv-row mb-7'>
                    <label className='form-label fw-bolder text-dark fs-6'>Email</label>
                    <input
                        placeholder='Email'
                        type='email'
                        autoComplete='off'
                        {...formik.getFieldProps('email')}
                        className={clsx(
                            'form-control form-control-lg form-control-solid',
                            { 'is-invalid': formik.touched.email && formik.errors.email },
                            {
                                'is-valid': formik.touched.email && !formik.errors.email,
                            }
                        )}
                    />
                    {formik.touched.email && formik.errors.email && (
                        <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                                <span role='alert'>{formik.errors.email}</span>
                            </div>
                        </div>
                    )}
                </div>
                {/* end::Form group */}

                {/* begin::Form group Code */}
                <div className='fv-row mb-7'>
                    <label className='form-label fw-bolder text-dark fs-6'>Verification Code</label>
                    <input
                        placeholder='Code'
                        type='text'
                        autoComplete='off'
                        {...formik.getFieldProps('code')}
                        className={clsx(
                            'form-control form-control-lg form-control-solid',
                            { 'is-invalid': formik.touched.code && formik.errors.code },
                            {
                                'is-valid': formik.touched.code && !formik.errors.code,
                            }
                        )}
                    />
                    {formik.touched.code && formik.errors.code && (
                        <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                                <span role='alert'>{formik.errors.code}</span>
                            </div>
                        </div>
                    )}
                </div>
                {/* end::Form group */}

                {/* begin::Form group Password */}
                <div className='mb-10 fv-row' data-kt-password-meter='true'>
                    <div className='mb-1'>
                        <label className='form-label fw-bolder text-dark fs-6'>Password</label>
                        <div className='position-relative mb-3'>
                            <div className='input-group'>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    {...formik.getFieldProps('password')}
                                    className={clsx(
                                        'form-control form-control-lg form-control-solid',
                                        'py-3 px-4',
                                        'rounded-2',
                                        'fs-6',
                                        { 'is-invalid': formik.touched.password && formik.errors.password },
                                        { 'is-valid': formik.touched.password && !formik.errors.password }
                                    )}
                                />
                                <button
                                    title={showPassword ? 'hide password' : 'show password'}
                                    type='button'
                                    className='btn btn-outline-secondary'
                                    onClick={togglePasswordVisibility}
                                >
                                    <i className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
                                </button>
                            </div>
                            <div className='alert alert-light mt-2 p-3' style={{fontSize: '0.85rem'}}>
                                <strong>Password Requirements:</strong>
                                <ul className='mb-0 mt-1 ps-4'>
                                    <li>At least 8 characters long</li>
                                    <li>Contains uppercase and lowercase letters</li>
                                    <li>Contains at least one number</li>
                                    <li>Contains at least one special character (!@#$%^&*(),.?":{}|&lt;&gt;)</li>
                                </ul>
                            </div>
                            {formik.touched.password && formik.errors.password && (
                                <div className='fv-plugins-message-container'>
                                    <div className='fv-help-block'>
                                        <span role='alert'>{formik.errors.password}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* end::Form group */}

                {/* begin::Form group Confirm password */}
                <div className='fv-row mb-5'>
                    <label className='form-label fw-bolder text-dark fs-6'>Confirm Password</label>
                    <div className='input-group'>
                        <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            {...formik.getFieldProps('changepassword')}
                            className={clsx(
                                'form-control form-control-lg form-control-solid',
                                'py-3 px-4',
                                'rounded-2',
                                'fs-6',
                                { 'is-invalid': formik.touched.changepassword && formik.errors.changepassword },
                                { 'is-valid': formik.touched.changepassword && !formik.errors.changepassword }
                            )}
                        />
                        <button
                            title={showPassword ? 'hide password' : 'show password'}
                            type='button'
                            className='btn btn-outline-secondary'
                            onClick={toggleConfirmPasswordVisibility}
                        >
                            <i className={showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
                        </button>
                    </div>
                    {formik.touched.changepassword && formik.errors.changepassword && (
                        <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                                <span role='alert'>{formik.errors.changepassword}</span>
                            </div>
                        </div>
                    )}
                </div>
                {/* end::Form group */}

                {/* begin::Form group */}
                <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
                    <button
                        type='submit'
                        id='kt_password_reset_submit'
                        className='btn btn-lg btn-primary fw-bolder me-4'
                    >
                        <span className='indicator-label'>Submit</span>
                        {loading && (
                            <span className='indicator-progress'>
                                Please wait...
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </span>
                        )}
                    </button>
                    <Link to='/auth/login'>
                        <button
                            type='button'
                            id='kt_login_password_reset_form_cancel_button'
                            className='btn btn-lg btn-light-primary fw-bolder'
                            disabled={formik.isSubmitting || !formik.isValid}
                        >
                            Cancel
                        </button>
                    </Link>{' '}
                </div>
                {/* end::Form group */}
            </form>
        </div>
    )
}
